<script setup lang="ts">
/**
 * The team on screen's Settings: its name and kind of board, which views its
 * switcher offers, who is in it and who else may change this, and emailed
 * invites (server/teamSettings.ts). Last in the switcher, and only for the
 * team's admins — the server's rule too.
 *
 * Every change is saved as it is made, bar the name, and comes back to every
 * member's tab as a `teams` event, which is what this page draws from.
 */
import { computed, ref, watch } from "vue";
import { api } from "../lib/api.js";
import { useBoard } from "../stores/board.js";
import { VIEW_LABELS } from "../stores/viewport.js";
import { toast } from "../lib/toast.js";
import { copyText } from "../lib/clipboard.js";
import { HIDEABLE_VIEWS, inviteEmail, type HideableView } from "@shared/teamViews.js";
import type { Team, TeamInvite, User } from "@shared/types.js";

const board = useBoard();
const team = computed(() => board.team);

/** What each view is, for the reader deciding whether the team needs it. */
const VIEW_HINTS: Partial<Record<HideableView, string>> = {
  shopping: "only while the board keeps a shopping list",
  repos: "site admins only, on a coding board",
  automation: "site admins only, on a coding board",
  tmux: "site admins only, on a coding board",
};

const error = ref("");
const busy = ref(false);

async function run<T>(work: () => Promise<T>): Promise<T | undefined> {
  busy.value = true;
  error.value = "";
  try {
    return await work();
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err);
    return undefined;
  } finally {
    busy.value = false;
  }
}

// ── the team ───────────────────────────────────────────────────────────────
const nameDraft = ref("");
watch(() => team.value?.name, (n) => (nameDraft.value = n ?? ""), { immediate: true });
const nameChanged = computed(() => !!team.value && nameDraft.value.trim() !== "" && nameDraft.value.trim() !== team.value.name);

function patch(body: Partial<Pick<Team, "name" | "coding" | "shopping" | "hiddenViews">>): Promise<unknown> {
  const id = team.value!.id;
  return run(() => api.patch(`/api/teams/${id}`, body));
}

async function saveName(): Promise<void> {
  if (nameChanged.value && (await patch({ name: nameDraft.value.trim() })) !== undefined) toast("Team renamed");
}

function shown(view: HideableView): boolean {
  return !team.value?.hiddenViews.includes(view);
}

function setShown(view: HideableView, on: boolean): void {
  const hidden = new Set(team.value!.hiddenViews);
  if (on) hidden.delete(view);
  else hidden.add(view);
  void patch({ hiddenViews: HIDEABLE_VIEWS.filter((v) => hidden.has(v)) });
}

// ── members ────────────────────────────────────────────────────────────────
const members = computed((): (User & { admin: boolean })[] =>
  board.teamMembers.map((u) => ({ ...u, admin: !!team.value?.adminIds.includes(u.id) })),
);

function setAdmin(user: User, admin: boolean): void {
  const id = team.value!.id;
  void run(() => api.put(`/api/teams/${id}/members/${user.id}`, { admin }));
}

/** Two clicks to take someone off: the first arms the button. */
const removing = ref<string | null>(null);
async function remove(user: User): Promise<void> {
  if (removing.value !== user.id) {
    removing.value = user.id;
    return;
  }
  removing.value = null;
  const id = team.value!.id;
  if ((await run(() => api.del(`/api/teams/${id}/members/${user.id}`))) !== undefined) {
    toast(user.id === board.me?.id ? `You left ${team.value?.name ?? "the team"}` : `${user.displayName} is no longer in the team`);
  }
}

// ── invites ────────────────────────────────────────────────────────────────
const invites = ref<TeamInvite[]>([]);
const email = ref("");
const emailOk = computed(() => inviteEmail(email.value) !== null);
/** A link the mail could not carry, for the admin to pass on themselves. */
const unsent = ref<{ email: string; link: string; why: string } | null>(null);

async function loadInvites(): Promise<void> {
  const id = team.value?.id;
  if (!id) return;
  const got = await run(() => api.get<{ invites: TeamInvite[] }>(`/api/teams/${id}/settings`));
  if (got && team.value?.id === id) invites.value = got.invites;
}
watch(() => team.value?.id, () => {
  invites.value = [];
  unsent.value = null;
  void loadInvites();
}, { immediate: true });

async function invite(): Promise<void> {
  if (!emailOk.value) return;
  const id = team.value!.id;
  const to = email.value.trim();
  const got = await run(() =>
    api.post<{ invite: TeamInvite; sent: boolean; mailError?: string; link?: string }>(`/api/teams/${id}/invites`, { email: to }),
  );
  if (!got) return;
  email.value = "";
  invites.value = [got.invite, ...invites.value.filter((i) => i.email !== got.invite.email)];
  if (got.sent) {
    unsent.value = null;
    toast(`Invite sent to ${got.invite.email}`);
  } else {
    unsent.value = { email: got.invite.email, link: got.link ?? "", why: got.mailError ?? "" };
  }
}

async function revoke(inv: TeamInvite): Promise<void> {
  const id = team.value!.id;
  const got = await run(() => api.del<{ invites: TeamInvite[] }>(`/api/teams/${id}/invites/${inv.id}`));
  if (got) invites.value = got.invites;
  if (unsent.value?.email === inv.email) unsent.value = null;
}

async function copyLink(): Promise<void> {
  if (!unsent.value) return;
  await copyText(unsent.value.link);
  toast("Invite link copied");
}

const inviter = (id: string): string => board.users.get(id)?.displayName ?? "someone";
const day = (ms: number): string => new Date(ms).toLocaleDateString(undefined, { day: "numeric", month: "short" });
</script>

<template>
  <section class="profile team-settings" aria-label="Team settings">
    <div v-if="team && board.isTeamAdmin" class="profile-inner">
      <header class="profile-head">
        <div>
          <h2>{{ team.name }} settings</h2>
          <div class="profile-sub">For everyone in {{ team.name }}. Only the team's admins can see or change them.</div>
        </div>
      </header>

      <p v-if="error" class="integration-error">{{ error }}</p>

      <div class="money-card profile-card">
        <h3>Team</h3>
        <label class="money-field">
          <span>Name</span>
          <input v-model="nameDraft" maxlength="64" :disabled="busy" @keydown.enter="saveName" />
        </label>
        <div class="money-actions">
          <button class="btn primary" type="button" :disabled="!nameChanged || busy" @click="saveName">Save</button>
        </div>
        <label class="profile-check">
          <input type="checkbox" :checked="team.coding" :disabled="busy" @change="patch({ coding: ($event.target as HTMLInputElement).checked })" />
          A coding team: tasks carry a repo and branch, and the board is filed by repo
        </label>
        <label class="profile-check">
          <input type="checkbox" :checked="team.shopping" :disabled="busy" @change="patch({ shopping: ($event.target as HTMLInputElement).checked })" />
          Keeps a shopping list
        </label>
      </div>

      <div class="money-card profile-card">
        <h3>Views</h3>
        <p class="money-hint">Which views the switcher offers on this team's board. The board itself, and these settings, are always there.</p>
        <label v-for="v in HIDEABLE_VIEWS" :key="v" class="profile-check">
          <input type="checkbox" :checked="shown(v)" :disabled="busy" @change="setShown(v, ($event.target as HTMLInputElement).checked)" />
          {{ VIEW_LABELS[v] }}
          <span v-if="VIEW_HINTS[v]" class="integration-muted">— {{ VIEW_HINTS[v] }}</span>
        </label>
      </div>

      <div class="money-card profile-card">
        <h3>Members</h3>
        <p class="money-hint">An admin can change these settings and invite people. Site admins in the team always can.</p>
        <ul class="team-members">
          <li v-for="m in members" :key="m.id">
            <span class="team-member-dot" :style="{ background: m.color }" aria-hidden="true"></span>
            <span class="team-member-name">{{ m.displayName }} <span class="integration-muted">@{{ m.username }}</span></span>
            <label class="profile-check team-member-admin" :title="m.isAdmin ? 'A site admin: always the team\'s admin' : ''">
              <input type="checkbox" :checked="m.admin || m.isAdmin" :disabled="busy || m.isAdmin" @change="setAdmin(m, ($event.target as HTMLInputElement).checked)" />
              Admin
            </label>
            <button
              class="btn ghost"
              type="button"
              :class="{ danger: removing === m.id }"
              :disabled="busy"
              @click="remove(m)"
              @blur="removing === m.id && (removing = null)"
            >{{ removing === m.id ? (m.id === board.me?.id ? "Leave?" : "Remove?") : m.id === board.me?.id ? "Leave" : "Remove" }}</button>
          </li>
        </ul>
      </div>

      <form class="money-card profile-card" @submit.prevent="invite">
        <h3>Invite</h3>
        <p class="money-hint">
          Emails a link to join {{ team.name }}. Someone without an account makes one from it. It works once, for two weeks;
          sending another to the same address replaces it.
        </p>
        <div class="integration-row">
          <input v-model="email" class="team-invite-email" type="email" placeholder="name@example.com" autocomplete="off" :disabled="busy" aria-label="Email address to invite" />
          <button class="btn primary" type="submit" :disabled="!emailOk || busy">Send invite</button>
        </div>
        <div v-if="unsent" class="team-invite-unsent">
          <p class="integration-error">The email to {{ unsent.email }} could not be sent: {{ unsent.why }}</p>
          <p class="money-hint">The invite is made — pass this link on yourself:</p>
          <div class="integration-row">
            <code class="integration-code team-invite-link">{{ unsent.link }}</code>
            <button class="btn ghost" type="button" @click="copyLink">Copy</button>
          </div>
        </div>
        <ul v-if="invites.length" class="team-invites">
          <li v-for="inv in invites" :key="inv.id">
            <span class="team-member-name">{{ inv.email }}</span>
            <span class="integration-muted">by {{ inviter(inv.invitedBy) }} on {{ day(inv.createdAt) }}, until {{ day(inv.expiresAt) }}</span>
            <button class="btn ghost" type="button" :disabled="busy" @click="revoke(inv)">Take back</button>
          </li>
        </ul>
        <p v-else class="integration-muted">No invites waiting.</p>
      </form>
    </div>
    <div v-else class="empty">Only the team's admins can change its settings.</div>
  </section>
</template>

<style scoped>
.team-members,
.team-invites {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.team-members li,
.team-invites li {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.team-member-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex: none;
}
.team-member-name {
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
}
.team-member-admin {
  margin: 0;
}
.team-invite-email {
  flex: 1;
  min-width: 0;
}
.team-invite-link {
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
  user-select: all;
}
.danger {
  color: var(--danger, #c33);
}
</style>
